import type { HelpArticleData } from '@workspace/api-backend'
import { FormField } from '@workspace/ui/components/form-field'
import { TextInput } from '@workspace/ui/components/text-input'
import { Textarea } from '@workspace/ui/components/textarea'
import { useState } from 'react'
import { TiptapEditor } from '../../../components/tiptap-editor'

interface HelpArticleFormProps {
  formId: string
  categories: { id: string; name: string }[]
  initialData?: HelpArticleData
  defaultCategoryId?: string
  onSubmit: (data: HelpArticleFormData) => void
}

export interface HelpArticleFormData {
  title: string
  slug?: string
  content: string
  description: string
  categoryId: string
  status: 'DRAFT' | 'PUBLISHED'
}

export function HelpArticleForm({
  formId,
  categories,
  initialData,
  defaultCategoryId,
  onSubmit,
}: HelpArticleFormProps) {
  const [title, setTitle] = useState(initialData?.title ?? '')
  const [slug, setSlug] = useState(initialData?.slug ?? '')
  const [content, setContent] = useState(initialData?.content ?? '')
  const [description, setDescription] = useState(initialData?.description ?? '')
  const [categoryId, setCategoryId] = useState(
    initialData?.categoryId ?? defaultCategoryId ?? categories[0]?.id ?? ''
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      title,
      slug: initialData ? slug : undefined,
      content,
      description: description || '',
      categoryId,
      status: initialData?.status ?? 'DRAFT',
    })
  }

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-6">
      <FormField label="Title" required>
        <TextInput
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Help article title"
          required
        />
      </FormField>

      {initialData && (
        <FormField label="Slug">
          <TextInput
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="url-friendly-slug"
          />
        </FormField>
      )}

      <FormField label="Category" required>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          required
          className="w-full rounded-md border border-border bg-input/30 px-2 py-1.5 text-sm text-foreground"
        >
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </FormField>

      <FormField label="Content" required>
        <TiptapEditor
          content={content}
          onChange={setContent}
          placeholder="Write your help article..."
        />
      </FormField>

      <FormField label="Description" hint="Shown in listings and search results. Max 500 chars">
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief summary of the article..."
          maxLength={500}
          rows={3}
        />
      </FormField>
    </form>
  )
}
